import type { SessionConversation, TimelineEntry } from './stream/types';

/**
 * Extracts the message_id for the assistant response at or near the given position.
 * The message_id is stored in the timeline entry's `id` field for text_item entries.
 * Returns undefined if no valid message_id is found.
 */
export const extractMessageId = (
  conversation: SessionConversation | undefined,
  position: number | undefined
): string | undefined => {
  if (!conversation || position === undefined) return undefined;

  // Find the text_item entry at the given position (or closest one before it)
  const textEntry = conversation.timeline
    .filter(
      (entry): entry is TimelineEntry & { type: 'text_item' } =>
        entry.type === 'text_item' && entry.position <= position
    )
    .pop();

  if (!textEntry) return undefined;

  // The entry id contains the message_id from the backend
  // Filter out fallback IDs that don't represent real backend message_ids
  const id = textEntry.id;
  if (!id || id.startsWith('text-') || id.startsWith('idx-')) {
    return undefined;
  }

  // Only accept real backend message_ids which start with "msg_"
  // This filters out client-generated random IDs from generateId() (pattern: {timestamp}-{random9chars})
  if (!id.startsWith('msg_')) {
    return undefined;
  }

  // Backend message IDs have format: "msg_{timestamp}_{index}" (e.g., "msg_1738058729.123_1")
  // When loading existing messages, convertChatMessages appends "-chunk-{n}" for deduplication
  // Strip this suffix to get the original backend ID for PostHog tracking
  return id.replace(/-chunk-\d+$/, '');
};
